import "ag-grid-enterprise";
import { Badge, Box, Button, Group, Modal, Stack, Switch, Text } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { useMemo, useRef, useState } from "react";
import { FaFileExcel } from "react-icons/fa6";
import { useAppSelector } from "../app/hooks";
import {
  selectRoomNumberingExportRows,
  type IRoomNumberingExportRow,
} from "./roomNumberingExportSelectors";

type RoomNumberingExportModalProps = {
  opened: boolean;
  onClose: () => void;
};

const sourceLabelByValue: Record<IRoomNumberingExportRow["roomNumberingValueSource"], string> = {
  manual: "Manuell",
  suggested: "Beregnet",
  "": "",
};

const formatSourceLabel = (value: unknown): string => {
  if (value === "manual" || value === "suggested" || value === "") {
    return sourceLabelByValue[value];
  }

  return "";
};

const ApprovalCellRenderer: React.FC<ICellRendererParams<IRoomNumberingExportRow, boolean>> = (
  params
) => (
  <Badge color={params.value ? "green" : "gray"} variant="light">
    {params.value ? "Ja" : "Nei"}
  </Badge>
);

export const RoomNumberingExportModal: React.FC<RoomNumberingExportModalProps> = ({
  opened,
  onClose,
}) => {
  const gridRef = useRef<AgGridReact<IRoomNumberingExportRow>>(null);
  const rows = useAppSelector(selectRoomNumberingExportRows);
  const [showApprovedOnly, setShowApprovedOnly] = useState(false);
  const visibleRows = useMemo(
    () => (showApprovedOnly ? rows.filter((row) => row.isApproved) : rows),
    [rows, showApprovedOnly]
  );
  const stats = useMemo(() => {
    const approved = rows.filter((row) => row.isApproved).length;
    const missing = rows.filter((row) => row.roomNumberingValue.length === 0).length;
    const manual = rows.filter((row) => row.roomNumberingValueSource === "manual").length;
    const calculated = rows.filter((row) => row.roomNumberingValueSource === "suggested").length;

    return {
      approved,
      missing,
      manual,
      calculated,
    };
  }, [rows]);
  const columnDefs = useMemo<ColDef<IRoomNumberingExportRow>[]>(
    () => [
      { field: "roomNumber", headerName: "Romnummer", pinned: "left", width: 130 },
      { field: "roomName", headerName: "Romnavn", minWidth: 180, flex: 1 },
      { field: "roomNumberingValue", headerName: "Eksportverdi", width: 140 },
      {
        field: "roomNumberingValueSource",
        headerName: "Kilde",
        width: 120,
        valueFormatter: (params) => formatSourceLabel(params.value),
      },
      { field: "suggestedRoomNumber", headerName: "Beregnet", width: 130 },
      { field: "manualRoomNumber", headerName: "Manuell verdi", width: 140 },
      {
        field: "isApproved",
        headerName: "Godkjent",
        width: 120,
        cellRenderer: ApprovalCellRenderer,
        valueFormatter: (params) => (params.value ? "Ja" : "Nei"),
      },
      { field: "approvedBy", headerName: "Godkjent av", minWidth: 160 },
      { field: "approvedAt", headerName: "Godkjent", minWidth: 190 },
      { field: "manualUpdatedBy", headerName: "Manuelt endret av", minWidth: 170 },
      { field: "manualUpdatedAt", headerName: "Manuelt endret", minWidth: 190 },
      { field: "runBy", headerName: "Kjørt av", minWidth: 160 },
      { field: "runAt", headerName: "Kjørt", minWidth: 190 },
    ],
    []
  );
  const defaultColDef = useMemo<ColDef<IRoomNumberingExportRow>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) =>
        params.value == null || params.value === "" ? "-" : params.value,
    }),
    []
  );
  const handleExportExcel = () => {
    gridRef.current?.api.exportDataAsExcel({
      fileName: `romnummerering-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Romnummerering",
      processCellCallback: (params) => {
        const colId = params.column.getColId();

        if (colId === "roomNumberingValueSource") {
          return formatSourceLabel(params.value);
        }

        if (colId === "isApproved") {
          return params.value ? "Ja" : "Nei";
        }

        return params.value ?? "";
      },
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Eksport romnummerering" size="95vw" centered>
      <Stack gap="xs" mb="sm">
        <Group justify="space-between" align="center">
          <Switch
            checked={showApprovedOnly}
            onChange={(event) => setShowApprovedOnly(event.currentTarget.checked)}
            label="Kun godkjente"
          />
          <Group gap="sm">
            <Button
              size="sm"
              leftSection={<FaFileExcel size={16} />}
              color="green"
              onClick={handleExportExcel}
            >
              Eksporter til Excel
            </Button>
            <Text size="sm" c="dimmed">
              {visibleRows.length} av {rows.length} rom
            </Text>
          </Group>
        </Group>
        <Group gap="xs">
          <Badge variant="light" color="green">
            Godkjent: {stats.approved}
          </Badge>
          <Badge variant="light" color="red">
            Mangler: {stats.missing}
          </Badge>
          <Badge variant="light" color="blue">
            Manuelt angitt: {stats.manual}
          </Badge>
          <Badge variant="light" color="gray">
            Beregnet: {stats.calculated}
          </Badge>
        </Group>
      </Stack>

      <Box className="ag-theme-quartz" h="70vh" w="100%">
        <AgGridReact<IRoomNumberingExportRow>
          ref={gridRef}
          rowData={visibleRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.roomNumber}
          animateRows
          overlayNoRowsTemplate="<span style='padding:8px;'>Ingen rom</span>"
        />
      </Box>
    </Modal>
  );
};
